import React from 'react';
import styled from 'styled-components';
import { line, space } from '@repo/design-tokens';
import { Button, Card, Icon, Kbd } from '@repo/design-system';

import { ZoomPercent, type ZoomReadout } from './zoomReadout';

// The store lives with the readout; re-exported for the step, which creates the picture's.
export { createZoomReadout } from './zoomReadout';
export type { ZoomReadout } from './zoomReadout';

/**
 * The picture's dock, bottom-right (`observers-page-camera-stage`, FR11; card 22's picture dock):
 * Refresh, Recentre (R), the zoom readout, and the button for the shortcut card (also `?`).
 * No pan mode and no zoom buttons: drag or the arrows pan, the wheel or + and − zoom. It does not drag
 * itself: `MovableCard` moves it and hands it the `grip`, drawn leftmost (`observers-page-motion`,
 * v3 L1764).
 */
export const PictureDock: React.FC<{
  grip?: React.ReactNode;
  zoom: ZoomReadout;
  onRecentre: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  shortcutsOpen: boolean;
  onToggleShortcuts: () => void;
  shortcutsId: string;
}> = ({
  grip,
  zoom,
  onRecentre,
  onRefresh,
  refreshing,
  shortcutsOpen,
  onToggleShortcuts,
  shortcutsId,
}) => (
  <Root surface="on-scene" data-control="">
    {grip}
    {onRefresh && (
      <Button
        variant="ghost"
        size="xs"
        loading={refreshing}
        iconLeft={<Icon name="arrows-rotate" variant="solid" />}
        onClick={onRefresh}
      >
        Refresh
      </Button>
    )}
    <Button
      variant="ghost"
      size="xs"
      aria-label="Recentre picture"
      iconLeft={<Icon name="crosshairs" variant="solid" />}
      onClick={onRecentre}
    >
      Recentre <Kbd keys="R" />
    </Button>
    <Divider aria-hidden="true" />
    <Zoom zoom={zoom} />
    <Button
      variant="ghost"
      size="xs"
      iconOnly
      aria-label="Keyboard shortcuts"
      aria-expanded={shortcutsOpen}
      aria-controls={shortcutsId}
      iconLeft={<Icon name="keyboard" />}
      onClick={onToggleShortcuts}
    />
  </Root>
);

const Root = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${space[1]};
  pointer-events: auto;
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  background: ${line[1]};
`;

const Zoom = styled(ZoomPercent)`
  padding: 0 ${space[1]};
`;
